import { useMutation } from '@tanstack/react-query';
import { UpdateOrganizationRecordingRetentionDto } from 'models/organization/dto/organization.dto';
import { useApiClient } from '../../../apiClient';
import { useSelectedOrganization } from './useSelectedOrganization';

export type RecordingRetentionResponse = {
    recordingRetentionDays: number;
    recordingRetentionIsDefault: boolean;
    recordingRetentionMaxDays: number;
};

// Per-org court-recording retention. Hits POST :id/recording-retention —
// elevated (platform) admins only; the endpoint 403s regular operators and
// 400s anything but a whole number in 1..max or null (= platform default).
// The response carries the EFFECTIVE values, which we patch into the cached
// selectedOrganization. Mirrors useUpdateOrganizationChatBackend.
export const useUpdateOrganizationRecordingRetention = (
    organizationId: string,
    onSuccess: () => void,
) => {
    const apiClient = useApiClient();
    const { selectedOrganization, setSelectedOrganization } = useSelectedOrganization();

    return useMutation({
        mutationKey: ['updateOrganizationRecordingRetention', organizationId],
        mutationFn: (body: UpdateOrganizationRecordingRetentionDto) =>
            apiClient.fetchApi<RecordingRetentionResponse, UpdateOrganizationRecordingRetentionDto>({
                method: 'POST',
                endpoint: `organizations/${organizationId}/recording-retention`,
                body,
            }),
        onSuccess: (data) => {
            onSuccess();
            // Only patch the cache for the org still in view — the operator
            // may have switched orgs while the request was in flight.
            if (selectedOrganization && organizationId === selectedOrganization.id) {
                setSelectedOrganization({
                    ...selectedOrganization,
                    recordingRetentionDays: data.recordingRetentionDays,
                    recordingRetentionIsDefault: data.recordingRetentionIsDefault,
                    recordingRetentionMaxDays: data.recordingRetentionMaxDays,
                });
            }
        },
    });
};
